import React, { useEffect, useRef, useState } from 'react';
import { collection, getDocs, limit, orderBy, query, where } from 'firebase/firestore';
import { auth, db } from '../firebase';
import BotaoAjuda from './BotaoAjuda';
import {
  abrirSeletorImagem,
  obterImagemDaAreaDeTransferencia,
  obterImagemDoEventoDeColagem,
} from '../filePicker';

const LIMITE_OFERTAS_COMPARTILHAMENTO = 120;
function normalizarTextoComparacao(valor) {
  return String(valor || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();
}

function obterEmojiCategoria(nomeCategoria) {
  const categoria = normalizarTextoComparacao(nomeCategoria);

  if (categoria.includes('acougue')) {
    return '🥩';
  }

  if (categoria.includes('hortifruti')) {
    return '🍎';
  }

  if (categoria.includes('frios') || categoria.includes('laticinio')) {
    return '🧀';
  }

  if (categoria.includes('padaria')) {
    return '🥖';
  }

  if (categoria.includes('mercearia') || categoria.includes('despensa')) {
    return '🥫';
  }

  if (categoria.includes('cerveja')) {
    return '🍻';
  }

  if (categoria.includes('destilado')) {
    return '🥃';
  }

  if (categoria.includes('vinho')) {
    return '🍷';
  }

  if (categoria.includes('suco')) {
    return '🧃';
  }

  if (categoria.includes('limpeza')) {
    return '🧹';
  }

  if (categoria.includes('refrigerante')) {
    return '🥤';
  }

  if (categoria.includes('agua')) {
    return '💧';
  }

  if (categoria.includes('higiene bucal')) {
    return '🦷';
  }

  if (categoria.includes('skincare') || categoria.includes('skin care')) {
    return '✨';
  }

  if (categoria.includes('cabelo')) {
    return '💇‍♀️';
  }

  if (categoria.includes('maquiagem')) {
    return '💄';
  }

  if (categoria.includes('perfumaria')) {
    return '🌸';
  }

  if (categoria.includes('banho') || categoria.includes('banho e higiene')) {
    return '🛁';
  }

  if (categoria.includes('unha')) {
    return '💅';
  }

  if (categoria.includes('suplemento') || categoria.includes('vitamina')) {
    return '💊';
  }

  if (categoria.includes('cuidados infantis')) {
    return '🍼';
  }

  if (categoria.includes('cosmetico')) {
    return '🧴';
  }

  if (categoria.includes('bazar') || categoria.includes('utilidade')) {
    return '🍳';
  }

  if (categoria === 'leite' || categoria.includes(' leite')) {
    return '🥛';
  }

  if (categoria.includes('fralda')) {
    return '🍼';
  }

  if (categoria.includes('pet')) {
    return '🐾';
  }

  if (categoria.includes('higiene pessoal')) {
    return '🧼';
  }

  if (categoria.includes('higiene')) {
    return '🧼';
  }

  if (categoria.includes('farmacia')) {
    return '💊';
  }

  if (categoria.includes('congelado')) {
    return '❄️';
  }

  return '🏷️';
}

function converterPrecoParaNumero(valor) {
  if (typeof valor === 'number') {
    return Number.isFinite(valor) ? valor : 0;
  }

  const textoOriginal = String(valor ?? '').trim();
  if (!textoOriginal) {
    return 0;
  }

  let textoLimpo = textoOriginal
    .replace(/\s/g, '')
    .replace(/R\$/gi, '')
    .replace(/\u00A0/g, '')
    .replace(/[^0-9,.-]/g, '');

  if (!textoLimpo) {
    return 0;
  }

  if (textoLimpo.includes(',') && textoLimpo.includes('.')) {
    if (textoLimpo.lastIndexOf(',') > textoLimpo.lastIndexOf('.')) {
      textoLimpo = textoLimpo.replace(/\./g, '').replace(',', '.');
    } else {
      textoLimpo = textoLimpo.replace(/,/g, '');
    }
  } else if (textoLimpo.includes(',')) {
    textoLimpo = textoLimpo.replace(/\./g, '').replace(',', '.');
  }

  const numero = Number(textoLimpo);
  return Number.isFinite(numero) ? numero : 0;
}

function formatarValorMonetario(valor) {
  return converterPrecoParaNumero(valor).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });
}

async function copiarTextoParaAreaDeTransferencia(texto) {
  if (navigator?.clipboard?.writeText) {
    await navigator.clipboard.writeText(texto);
    return true;
  }

  const campoTemporario = document.createElement('textarea');
  campoTemporario.value = texto;
  campoTemporario.setAttribute('readonly', 'readonly');
  campoTemporario.style.position = 'fixed';
  campoTemporario.style.opacity = '0';
  campoTemporario.style.pointerEvents = 'none';
  document.body.appendChild(campoTemporario);
  campoTemporario.focus();
  campoTemporario.select();

  try {
    const copiado = document.execCommand('copy');
    document.body.removeChild(campoTemporario);
    return copiado;
  } catch (error) {
    document.body.removeChild(campoTemporario);
    throw error;
  }
}

function montarMensagemWhatsApp({ nomeLoja, cidade, estado, ofertas }) {
  const agrupado = new Map();

  ofertas.forEach((oferta) => {
    const categoria = String(oferta.nome_tag || 'Ofertas').trim() || 'Ofertas';

    if (!agrupado.has(categoria)) {
      agrupado.set(categoria, []);
    }

    agrupado.get(categoria).push(oferta);
  });

  const categoriasOrdenadas = Array.from(agrupado.entries()).sort((a, b) =>
    a[0].localeCompare(b[0], 'pt-BR', { sensitivity: 'base' }),
  );
  const localLoja = [cidade, estado].filter(Boolean).join(' - ');
  const linhas = [
    `*${nomeLoja}*`,
    localLoja || null,
    `${ofertas.length} oferta${ofertas.length === 1 ? ' dispon\u00edvel' : 's dispon\u00edveis'} no momento.`,
    '',
  ].filter(Boolean);

  categoriasOrdenadas.forEach(([categoria, itens]) => {
    linhas.push(`${obterEmojiCategoria(categoria)} *${categoria}*`);

    itens
      .slice()
      .sort((a, b) =>
        String(a.nome_produto || '').localeCompare(String(b.nome_produto || ''), 'pt-BR', { sensitivity: 'base' }),
      )
      .forEach((item) => {
        linhas.push(`- ${item.nome_produto} - ${formatarValorMonetario(item.preco)}`);
      });

    linhas.push('');
  });

  linhas.push('_Consulte mais ofertas no app PromoAlert._');

  return linhas.join('\n').trim();
}

const usePreviewUrl = (arquivo) => {
  const [previewUrl, setPreviewUrl] = useState('');

  useEffect(() => {
    if (!arquivo) {
      setPreviewUrl('');
      return undefined;
    }

    const objectUrl = URL.createObjectURL(arquivo);
    setPreviewUrl(objectUrl);

    return () => URL.revokeObjectURL(objectUrl);
  }, [arquivo]);

  return previewUrl;
};

const ZonaArrasto = ({ p, handleLoteChange }) => {
  const [isDragging, setIsDragging] = useState(false);
  const inputRef = useRef(null);
  const previewUrl = usePreviewUrl(p.imagem);

  const abrirSeletorArquivo = async () => {
    await abrirSeletorImagem({
      inputRef,
      onFileSelecionado: (arquivo) => handleLoteChange(p.id_temp, 'imagem', arquivo),
    });
  };

  const aplicarImagem = (arquivo) => {
    if (!arquivo?.type?.startsWith('image/')) {
      alert('Escolha ou cole um arquivo de imagem valido.');
      return;
    }
    handleLoteChange(p.id_temp, 'imagem', arquivo);
  };

  const colarImagem = async (evento) => {
    evento?.stopPropagation();
    try {
      const arquivo = await obterImagemDaAreaDeTransferencia();
      if (!arquivo) {
        alert('Nenhuma imagem foi encontrada. Copie uma imagem e tente novamente.');
        return;
      }
      aplicarImagem(arquivo);
    } catch (error) {
      console.warn('Nao foi possivel ler a imagem copiada:', error);
      alert('Nao foi possivel acessar a imagem copiada. Clique na area da foto e use Ctrl+V.');
    }
  };

  const tratarColagem = (evento) => {
    const arquivo = obterImagemDoEventoDeColagem(evento);
    if (!arquivo) return;
    evento.preventDefault();
    aplicarImagem(arquivo);
  };

  const tratarTeclaSeletor = (e) => {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    e.preventDefault();
    void abrirSeletorArquivo();
  };

  const onDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const onDragLeave = () => setIsDragging(false);

  const onDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      aplicarImagem(e.dataTransfer.files[0]);
    }
  };

  if (p.imagem && previewUrl) {
    return (
      <div className="routine-image-picker routine-image-picker--small">
      <div className="routine-thumb-preview routine-thumb-preview--small" onPaste={tratarColagem} tabIndex={0}>
        <img
          src={previewUrl}
          alt="Pré-visualização"
          className="routine-thumb-preview__image"
        />
        <button
          type="button"
          className="routine-thumb-preview__remove"
          onClick={() => {
            handleLoteChange(p.id_temp, 'imagem', null);
            if (inputRef.current) inputRef.current.value = '';
          }}
        >
          x
        </button>
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          onChange={(e) => aplicarImagem(e.target.files[0])}
          style={{ display: 'none' }}
        />
      </div>
      <button type="button" className="routine-image-paste-button routine-image-paste-button--small" onClick={colarImagem}>
        Colar
      </button>
      </div>
    );
  }

  return (
    <div className="routine-image-picker routine-image-picker--small">
    <div
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      onPaste={tratarColagem}
      onClick={() => {
        void abrirSeletorArquivo();
      }}
      onKeyDown={tratarTeclaSeletor}
      role="button"
      tabIndex={0}
      className={`routine-dropzone routine-dropzone--small ${isDragging ? 'is-dragging' : ''}`}
    >
      <span className="routine-dropzone__glyph">+</span>
      <span className="routine-dropzone__label">Foto</span>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        onChange={(e) => aplicarImagem(e.target.files[0])}
        style={{ display: 'none' }}
      />
    </div>
    <button type="button" className="routine-image-paste-button routine-image-paste-button--small" onClick={colarImagem}>
      Colar
    </button>
    </div>
  );
};

function AbaPostagemLote({
  planoAtual,
  postagensWebNaSemana,
  cotaMaxWeb,
  postagensMensaisGeral,
  limMensalGratuitoVisual,
  loteProdutos,
  handleLoteChange,
  handleLoteMultiChange,
  produtosPadrao,
  buscarSugestoesCatalogo,
  podeUsarCatalogo,
  tags,
  removerLinhaLote,
  adicionarLinhaLote,
  isEnviandoLote,
  enviarLoteAoFirebase,
  minhasFiliais,
  usuarioAtual,
  podeSelecionarLojaNoLote,
  locaisDoLote,
  localSelecionadoNoLoteId,
  setLocalSelecionadoNoLoteId,
  progressoEnvio,
  aplicarDataATodos,
}) {
  const possuiFiliais = minhasFiliais && minhasFiliais.length > 0;
  const [dataGlobal, setDataGlobal] = useState('');
  const [isCompartilhandoWhatsApp, setIsCompartilhandoWhatsApp] = useState(false);
  const [linhaCatalogoAtiva, setLinhaCatalogoAtiva] = useState(null);
  const [consultaCatalogo, setConsultaCatalogo] = useState({});
  const [sugestoesCatalogo, setSugestoesCatalogo] = useState({});
  const [carregandoSugestoesCatalogo, setCarregandoSugestoesCatalogo] = useState({});
  const requisicaoCatalogoRef = useRef(0);
  const perfisAtivos = Array.isArray(usuarioAtual?.perfis_ativos) ? usuarioAtual.perfis_ativos : [];
  const ehLojista = perfisAtivos.includes('lojista') || usuarioAtual?.tipo_perfil === 'lojista';
  const podeCompartilharNoWhatsApp =
    ['ouro', 'diamante'].includes(String(planoAtual || '').toLowerCase()) && ehLojista;
  const compartilharEncarteNoWhatsApp = async () => {
    if (isCompartilhandoWhatsApp) return;

    const lojaId = usuarioAtual?.id || auth.currentUser?.uid;

    if (!lojaId) {
      alert('N\u00e3o foi poss\u00edvel identificar a loja para o compartilhamento.');
      return;
    }

    setIsCompartilhandoWhatsApp(true);
    try {
      const consulta = query(
        collection(db, 'promocoes'),
        where('id_usuario', '==', lojaId),
        orderBy('timestamp', 'desc'),
        limit(LIMITE_OFERTAS_COMPARTILHAMENTO),
      );
      const snapshot = await getDocs(consulta);

      const ofertasOficiais = snapshot.docs
        .map((item) => ({ id: item.id, ...item.data() }))
        .filter((item) => item.perfil_usuario === 'lojista' && item.nome_produto && item.preco !== undefined);

      if (ofertasOficiais.length === 0) {
        alert('Ainda n\u00e3o existem ofertas oficiais desta loja para compartilhar.');
        return;
      }

      const nomeLoja =
        String(usuarioAtual?.razaoSocial || '').trim() ||
        String(usuarioAtual?.nome || '').trim() ||
        'Minha loja';
      const cidadeLoja = usuarioAtual?.cidade_presenca || usuarioAtual?.cidade || '';
      const estadoLoja = usuarioAtual?.estado_presenca || usuarioAtual?.estado || '';
      const mensagem = montarMensagemWhatsApp({
        nomeLoja,
        cidade: cidadeLoja,
        estado: estadoLoja,
        ofertas: ofertasOficiais,
      });
      const copiadoComSucesso = await copiarTextoParaAreaDeTransferencia(mensagem);

      alert(
        copiadoComSucesso
          ? 'Texto copiado. Agora cole no seu WhatsApp com Ctrl + V.'
          : 'O texto foi preparado, mas a cópia automática não confirmou sucesso. Tente novamente.',
      );
    } catch (error) {
      console.error('Erro ao gerar compartilhamento no WhatsApp:', error);
      alert(`N\u00e3o foi poss\u00edvel preparar o compartilhamento agora: ${error.message}`);
    } finally {
      setIsCompartilhandoWhatsApp(false);
    }
  };

  const aplicarProdutoDoCatalogo = (idLinha, item) => {
    handleLoteMultiChange(idLinha, {
      nome_produto: item.nome_produto,
      id_tag: item.id_tag,
      url_imagem_padrao: item.url_imagem,
      usando_catalogo: true,
    });
    setConsultaCatalogo((prev) => ({ ...prev, [idLinha]: item.nome_produto }));
    setLinhaCatalogoAtiva(null);
  };

  const obterSugestoesLocais = (idLinha, nomeAtual, categoriaId) => {
    const termo = normalizarTextoComparacao(consultaCatalogo[idLinha] ?? nomeAtual ?? '');

    return produtosPadrao
      .filter((item) => !categoriaId || item.id_tag === categoriaId)
      .filter((item) => {
        if (!termo) return true;
        return normalizarTextoComparacao(item.nome_produto).includes(termo);
      })
      .slice(0, 5);
  };

  useEffect(() => {
    if (!podeUsarCatalogo || linhaCatalogoAtiva === null) return undefined;

    const produtoAtual = loteProdutos.find((item) => item.id_temp === linhaCatalogoAtiva);
    if (!produtoAtual) return undefined;

    const termo = String(consultaCatalogo[linhaCatalogoAtiva] ?? produtoAtual.nome_produto ?? '').trim();
    const categoriaId = produtoAtual.id_tag || '';
    const precisaCargaInicial = produtosPadrao.length === 0 && termo.length === 0 && !categoriaId;
    const deveBuscarNoServidor = termo.length >= 2 || Boolean(categoriaId) || precisaCargaInicial;

    if (!deveBuscarNoServidor) {
      setSugestoesCatalogo((atual) => ({
        ...atual,
        [linhaCatalogoAtiva]: obterSugestoesLocais(linhaCatalogoAtiva, produtoAtual.nome_produto, categoriaId),
      }));
      setCarregandoSugestoesCatalogo((atual) => ({ ...atual, [linhaCatalogoAtiva]: false }));
      return undefined;
    }

    const idRequisicao = ++requisicaoCatalogoRef.current;
    const temporizador = setTimeout(async () => {
      setCarregandoSugestoesCatalogo((atual) => ({ ...atual, [linhaCatalogoAtiva]: true }));

      try {
        const itens = await buscarSugestoesCatalogo({ termo, categoriaId });
        if (requisicaoCatalogoRef.current !== idRequisicao) return;

        setSugestoesCatalogo((atual) => ({ ...atual, [linhaCatalogoAtiva]: itens }));
      } catch (error) {
        console.error('Erro ao buscar sugestões do catálogo:', error);
        if (requisicaoCatalogoRef.current !== idRequisicao) return;

        setSugestoesCatalogo((atual) => ({ ...atual, [linhaCatalogoAtiva]: [] }));
      } finally {
        if (requisicaoCatalogoRef.current === idRequisicao) {
          setCarregandoSugestoesCatalogo((atual) => ({ ...atual, [linhaCatalogoAtiva]: false }));
        }
      }
    }, 250);

    return () => clearTimeout(temporizador);
  }, [buscarSugestoesCatalogo, linhaCatalogoAtiva, loteProdutos, podeUsarCatalogo, planoAtual, produtosPadrao, consultaCatalogo]);

  const obterSugestoesVisiveis = (idLinha, nomeAtual, categoriaId) =>
    sugestoesCatalogo[idLinha] ?? obterSugestoesLocais(idLinha, nomeAtual, categoriaId);

  return (
    <div className="routine-panel">
      <div className="routine-toolbar">
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
          <div className="routine-inline-card routine-inline-card--accent">
            <span className="routine-inline-card__label">Validade Global</span>
            <input
              type="date"
              value={dataGlobal}
              onChange={(e) => setDataGlobal(e.target.value)}
              disabled={isEnviandoLote}
              className="routine-input"
              style={{ minHeight: '38px', width: '150px', padding: '0 10px' }}
            />
            <button
              type="button"
              onClick={() => aplicarDataATodos(dataGlobal)}
              disabled={!dataGlobal || isEnviandoLote}
              className="routine-btn routine-btn--soft"
              style={{ minHeight: '38px' }}
            >
              Aplicar a Todas
            </button>
          </div>

          {podeSelecionarLojaNoLote && (
            <label className="routine-inline-card" style={{ minWidth: '280px' }}>
              <span className="routine-inline-card__label">Loja do lote</span>
              <select
                value={localSelecionadoNoLoteId}
                onChange={(event) => setLocalSelecionadoNoLoteId(event.target.value)}
                disabled={isEnviandoLote}
                className="routine-select"
                aria-label="Loja cadastrada para este lote"
              >
                <option value="">Selecione uma loja...</option>
                {locaisDoLote.map((local) => (
                  <option key={local.id} value={local.id}>
                    {[local.nome, local.cidade, local.estado].filter(Boolean).join(' - ')}
                  </option>
                ))}
              </select>
            </label>
          )}

          <div className="routine-inline-card">
            <span className="routine-inline-card__label">Plano</span>
            <span className="routine-inline-card__value">{planoAtual.toUpperCase()}</span>
            <span className="routine-inline-card__label">Lote</span>
            <span className="routine-inline-card__value">
              {postagensWebNaSemana} / {cotaMaxWeb >= 9999 ? '∞' : cotaMaxWeb}
            </span>
          </div>

          {planoAtual === 'gratuito' && (
            <div className="routine-inline-card" style={{ borderColor: 'rgba(248, 113, 113, 0.24)', background: '#fff5f5' }}>
              <span className="routine-inline-card__label">Total do Mês</span>
              <span className="routine-inline-card__value" style={{ color: '#b91c1c' }}>
                {postagensMensaisGeral} / {limMensalGratuitoVisual}
              </span>
            </div>
          )}

          {podeCompartilharNoWhatsApp && (
            <button
              type="button"
              onClick={compartilharEncarteNoWhatsApp}
              disabled={isCompartilhandoWhatsApp || isEnviandoLote}
              className="routine-btn routine-btn--diamond"
            >
              {isCompartilhandoWhatsApp ? 'Preparando texto...' : 'Copiar texto para WhatsApp'}
            </button>
          )}

          <BotaoAjuda tela="postagemLote" />
        </div>
      </div>

      {isEnviandoLote && (
        <div className="routine-progress">
          <h4 className="routine-progress__title">Processando o lote</h4>
          <p className="routine-progress__text">{progressoEnvio}</p>
        </div>
      )}

      <div
        className="routine-table-wrap routine-table-wrap--postagem"
        style={{
          marginTop: '20px',
          opacity: isEnviandoLote ? 0.6 : 1,
          pointerEvents: isEnviandoLote ? 'none' : 'auto',
        }}
      >
        <table className="routine-table routine-table--postagem">
          <thead>
            <tr>
              <th style={{ width: possuiFiliais ? '9%' : '10%', textAlign: 'center' }}>Foto *</th>
              <th style={{ width: possuiFiliais ? '26%' : '40%' }}>Nome do Produto *</th>
              <th style={{ width: possuiFiliais ? '10%' : '12%' }}>Preço (R$) *</th>
              <th style={{ width: possuiFiliais ? '12%' : '16%' }}>Categoria *</th>
              <th style={{ width: '14%' }}>Validade</th>
              {possuiFiliais && <th style={{ width: '21%' }}>Lojas Destino</th>}
              <th style={{ width: '8%', textAlign: 'center' }}>Ação</th>
            </tr>
          </thead>
          <tbody>
            {loteProdutos.map((produto) => {
              const idMatriz = usuarioAtual?.id || usuarioAtual?.uid;
              const lojasAtuais = [usuarioAtual, ...(produto.lojas_selecionadas || [])]
                .filter((loja, index, lojas) => {
                  const lojaId = loja?.id || loja?.uid;
                  return lojaId && lojas.findIndex((item) => (item?.id || item?.uid) === lojaId) === index;
                });
              const todasSelecionadas = minhasFiliais.every((filial) =>
                lojasAtuais.some((loja) => (loja.id || loja.uid) === filial.id),
              );

              return (
                <tr key={produto.id_temp}>
                  <td style={{ textAlign: 'center' }}>
                    {produto.usando_catalogo ? (
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px' }}>
                        <img
                          src={produto.url_imagem_padrao}
                          alt="Catálogo"
                          className="routine-thumb"
                        />
                        <span className="routine-status routine-status--success">Catálogo</span>
                      </div>
                    ) : (
                      <ZonaArrasto p={produto} handleLoteChange={handleLoteChange} />
                    )}
                  </td>

                  <td>
                    <div className="routine-autocomplete">
                      <input
                        type="text"
                        placeholder={podeUsarCatalogo ? 'Digite ou selecione do catálogo...' : 'Digite o nome...'}
                        value={produto.nome_produto}
                        onFocus={() => {
                          if (podeUsarCatalogo) {
                            setLinhaCatalogoAtiva(produto.id_temp);
                            setConsultaCatalogo((prev) => ({
                              ...prev,
                              [produto.id_temp]: prev[produto.id_temp] ?? produto.nome_produto ?? '',
                            }));
                          }
                        }}
                        onDoubleClick={() => {
                          if (podeUsarCatalogo) {
                            setLinhaCatalogoAtiva(produto.id_temp);
                            setConsultaCatalogo((prev) => ({
                              ...prev,
                              [produto.id_temp]: prev[produto.id_temp] ?? produto.nome_produto ?? '',
                            }));
                          }
                        }}
                        onBlur={() => {
                          if (podeUsarCatalogo) {
                            setTimeout(() => {
                              setLinhaCatalogoAtiva((atual) => (atual === produto.id_temp ? null : atual));
                            }, 160);
                          }
                        }}
                        onChange={(e) => {
                          const valor = e.target.value;

                          if (podeUsarCatalogo) {
                            setConsultaCatalogo((prev) => ({ ...prev, [produto.id_temp]: valor }));
                            setLinhaCatalogoAtiva(produto.id_temp);
                          }

                          handleLoteMultiChange(produto.id_temp, {
                            nome_produto: valor,
                            url_imagem_padrao: null,
                            usando_catalogo: false,
                          });
                        }}
                        className="routine-input"
                        style={{ textAlign: 'center' }}
                      />

                      {podeUsarCatalogo && linhaCatalogoAtiva === produto.id_temp && (
                        <div className="routine-autocomplete__panel">
                          <div className="routine-autocomplete__header">
                            <span className="routine-autocomplete__title">Sugestões do catálogo</span>
                            <span className="routine-autocomplete__hint">Você também pode digitar livremente</span>
                          </div>

                          <div className="routine-autocomplete__list">
                            {carregandoSugestoesCatalogo[produto.id_temp] ? (
                              <div className="routine-autocomplete__empty">
                                Buscando no catálogo...
                              </div>
                            ) : obterSugestoesVisiveis(produto.id_temp, produto.nome_produto, produto.id_tag).length > 0 ? (
                              obterSugestoesVisiveis(produto.id_temp, produto.nome_produto, produto.id_tag).map((item) => (
                                <button
                                  key={item.id}
                                  type="button"
                                  className="routine-autocomplete__option"
                                  onMouseDown={(e) => {
                                    e.preventDefault();
                                    aplicarProdutoDoCatalogo(produto.id_temp, item);
                                  }}
                                >
                                  <span className="routine-autocomplete__name">{item.nome_produto}</span>
                                  <span className="routine-autocomplete__meta">
                                    {tags.find((tag) => tag.id === item.id_tag)?.nome_tag || 'Sem categoria'}
                                  </span>
                                </button>
                              ))
                            ) : (
                              <div className="routine-autocomplete__empty">
                                Nenhum item do catálogo foi encontrado.
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </td>

                  <td>
                    <input
                      type="number"
                      step="0.01"
                      placeholder="29.90"
                      value={produto.preco}
                      onChange={(e) => handleLoteChange(produto.id_temp, 'preco', e.target.value)}
                      className="routine-input"
                    />
                  </td>

                  <td>
                    <select
                      value={produto.id_tag}
                      onChange={(e) => handleLoteChange(produto.id_temp, 'id_tag', e.target.value)}
                      className="routine-select"
                    >
                      <option value="">Escolha...</option>
                      {tags.map((tag) => (
                        <option key={tag.id} value={tag.id}>
                          {tag.nome_tag}
                        </option>
                      ))}
                    </select>
                  </td>

                  <td>
                    <input
                      type="date"
                      value={produto.data_validade || ''}
                      onChange={(e) => handleLoteChange(produto.id_temp, 'data_validade', e.target.value)}
                      className="routine-input"
                    />
                  </td>

                  {possuiFiliais && (
                    <td>
                      <div className="routine-postagem-destino">
                        <div className="routine-postagem-destino__head">
                          <span className="routine-inline-card__label routine-postagem-destino__label">Selecione o destino</span>
                          <button
                            type="button"
                            className="routine-btn routine-btn--soft"
                            style={{ minHeight: '32px', padding: '0 10px', fontSize: '11px' }}
                            onClick={() => {
                              if (todasSelecionadas) {
                                handleLoteChange(produto.id_temp, 'lojas_selecionadas', [usuarioAtual]);
                              } else {
                                handleLoteChange(produto.id_temp, 'lojas_selecionadas', [usuarioAtual, ...minhasFiliais]);
                              }
                            }}
                          >
                            {todasSelecionadas ? 'Desmarcar filiais' : 'Marcar tudo'}
                          </button>
                        </div>

                        <div className="routine-choice-list">
                          <label
                            className={`routine-choice-chip ${lojasAtuais.some((loja) => loja.id === usuarioAtual.id) ? 'is-selected' : ''}`}
                          >
                            <input
                              type="checkbox"
                              style={{ display: 'none' }}
                              checked={Boolean(idMatriz)}
                              readOnly
                            />
                            Matriz
                          </label>

                          {minhasFiliais.map((filial) => (
                            <label
                              key={filial.id}
                              className={`routine-choice-chip ${lojasAtuais.some((loja) => loja.id === filial.id) ? 'is-selected' : ''}`}
                            >
                              <input
                                type="checkbox"
                                style={{ display: 'none' }}
                                checked={lojasAtuais.some((loja) => loja.id === filial.id)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    handleLoteChange(produto.id_temp, 'lojas_selecionadas', [...lojasAtuais, filial]);
                                  } else {
                                    handleLoteChange(
                                      produto.id_temp,
                                      'lojas_selecionadas',
                                      lojasAtuais.filter((loja) => loja.id !== filial.id),
                                    );
                                  }
                                }}
                              />
                              {filial.nome || 'Filial'}
                            </label>
                          ))}
                        </div>
                      </div>
                    </td>
                  )}

                  <td>
                    <div className="routine-table__actions">
                      <button
                        type="button"
                        className="routine-btn routine-btn--danger"
                        onClick={() => removerLinhaLote(produto.id_temp)}
                      >
                        Remover
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
        <button
          type="button"
          onClick={adicionarLinhaLote}
          disabled={isEnviandoLote}
          className="routine-btn routine-btn--soft"
        >
          Nova Linha
        </button>

        <button
          type="button"
          onClick={enviarLoteAoFirebase}
          disabled={isEnviandoLote || loteProdutos.length === 0}
          className="routine-btn routine-btn--primary"
        >
          {isEnviandoLote ? 'Publicando...' : `Publicar ${loteProdutos.length} Oferta${loteProdutos.length === 1 ? '' : 's'}`}
        </button>
      </div>
    </div>
  );
}

export default AbaPostagemLote;

